import { styled } from "@mui/material";
import {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRowHeightParams,
  GridRowParams,
  GridSlotsComponentsProps,
  GridSortModel,
} from "@mui/x-data-grid";
import { DataGridPro, DataGridProProps, GRID_DETAIL_PANEL_TOGGLE_COL_DEF, useGridApiRef } from "@mui/x-data-grid-pro";
import { DownloadStatusIcon, HumanDate } from "..";
import useAuth, { DataGridColumns, DataGridPreferences, DataGrids } from "../../context/UserContext";
import { DownloadBundle } from "../../graphql/types";
import { calculateDateDuration, getFormattedReportDate, toDefaultInt, toLocalAndUtcDates } from "../../lib/Util";

import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import {
  CustomColumnsPanel,
  customColumnWidthChange,
  customOnColumnOrderChange,
  getInitialSortModel,
} from "../DataGridUtils";
import { DatagridEmptyState } from "../DatagridEmptyState/DatagridEmptyState";
import { IDownloadStatusIcon } from "../DownloadsComponents/DownloadStatusIcon";
import { getNameFromHandle, NameFromHandle, toPercentValue } from "../common/Formatters";
import { isFilteringDownloads } from "./DownloadFilters";
import { DownloadRowsForBundle, ExpandCell } from "./DownloadRowsForBundle";
import { DownloadsActionsMenu } from "./DownloadsActionsMenu";
import { PurposeCell } from "./PurposeCell";
import { IDownloadFilters } from "./downloadsDatagridUtils";

const CenteredDiv = styled("div")({
  display: "flex",
  alignItems: "center",
  height: "100%",
  width: "100%",
});

const getBundleRowCounts = (
  bundle: DownloadBundle
): Pick<
  IDownloadStatusIcon,
  "dirtyRowCount" | "rowCount" | "newDirtyRowCount" | "dirtyRowStatus" | "newDirtyRowStatus"
> | null => {
  if (!bundle?.parts?.length) return null;
  const part = bundle.parts[0];
  if (!part) return null;
  if (bundle?.parts?.length === 1) {
    return {
      dirtyRowCount: part?.dirty_row_count,
      dirtyRowStatus: part?.dirty_row_status,
      rowCount: part?.row_count,
      newDirtyRowCount: part?.new_dirty_row_count,
      newDirtyRowStatus: part?.new_dirty_row_status,
    };
  }
  return bundle.parts.reduce(
    (acc, item) => {
      if (!item) return acc;
      acc.dirtyRowCount += item.dirty_row_count || 0;
      acc.newDirtyRowCount += item.new_dirty_row_count || 0;
      acc.rowCount += item.row_count || 0;

      if (acc?.newDirtyRowStatus !== item.new_dirty_row_status) {
        acc.newDirtyRowStatus = "some";
      }

      if (acc.dirtyRowStatus !== item.dirty_row_status) {
        acc.dirtyRowStatus = "some";
      }

      return acc;
    },
    {
      dirtyRowCount: 0,
      newDirtyRowCount: 0,
      rowCount: 0,
      newDirtyRowStatus: part.new_dirty_row_status,
      dirtyRowStatus: part.dirty_row_status,
    }
  );
};

const EmptyRows = ({ isFiltering }: NonNullable<GridSlotsComponentsProps["noRowsOverlay"]>) => {
  return (
    <DatagridEmptyState
      Icon={InfoIcon}
      text={isFiltering ? "No results, try another filter" : "You have no downloads yet"}
    />
  );
};

type DownloadsDatagridProps = {
  filter: IDownloadFilters;
  rows: DownloadBundle[];
  onRowsScrollEnd: DataGridProProps["onRowsScrollEnd"];
  loading: boolean;
  onSortModelChange?: (model: GridSortModel) => void;
  excludeFields?: string[];
  handleRowClick?: GridEventListener<"rowClick">;
  datagridPreferencesElement: DataGridPreferences;
  initialVisibilityModel: { [key in DataGridColumns]?: boolean };
  preferenceElementToUpdate: DataGrids;
};

const DownloadsDataGrid = ({
  filter,
  rows,
  loading,
  onRowsScrollEnd,
  onSortModelChange,
  excludeFields,
  handleRowClick,
  datagridPreferencesElement,
  initialVisibilityModel,
  preferenceElementToUpdate,
}: DownloadsDatagridProps) => {
  const apiRef = useGridApiRef();
  const { user, updateDataGridPreferences } = useAuth();
  const columnsWidth = datagridPreferencesElement?.columnsWidth;
  const columnsAux: (GridColDef | undefined)[] = [
    excludeFields?.includes("report_datetime")
      ? undefined
      : {
          width: columnsWidth?.report_datetime || 150,
          field: "report_datetime",
          headerName: "Report Date",
          valueFormatter: (_, row: DownloadBundle) => {
            return getFormattedReportDate(row?.report_datetime?.iso, true);
          },
          valueGetter: (_value, row: DownloadBundle) => {
            return getFormattedReportDate(row?.report_datetime?.iso, true);
          },
          filterable: false,
        },
    excludeFields?.includes("created")
      ? undefined
      : {
          width: columnsWidth?.created || 170,
          field: "created",
          headerName: "Created",
          valueFormatter: (_, row: DownloadBundle) => {
            if (!row.id) {
              return;
            }
            return toLocalAndUtcDates(row?.created).browser;
          },
          renderCell: ({ row }: { row: DownloadBundle }) => {
            return (
              <span>
                <HumanDate date={row?.created} />
              </span>
            );
          },
          filterable: false,
        },
    excludeFields?.includes("started")
      ? undefined
      : {
          width: columnsWidth?.started || 170,
          field: "started",
          headerName: "Started",
          valueFormatter: (_, row: DownloadBundle) => {
            if (!row.id || !row.started) {
              return;
            }
            return toLocalAndUtcDates(row.started).browser;
          },
          renderCell: ({ row }: { row: DownloadBundle }) => {
            return (
              <span>
                <HumanDate date={row?.started} />
              </span>
            );
          },
          filterable: false,
        },
    excludeFields?.includes("finished")
      ? undefined
      : {
          width: columnsWidth?.finished || 170,
          field: "finished",
          headerName: "Finished",
          valueFormatter: (_, row: DownloadBundle) => {
            if (!row.id || !row.finished) {
              return;
            }
            return toLocalAndUtcDates(row.finished).browser;
          },
          renderCell: ({ row }: { row: DownloadBundle }) => {
            return (
              <span>
                <HumanDate date={row?.finished} />
              </span>
            );
          },
          filterable: false,
        },
    excludeFields?.includes("download_duration")
      ? undefined
      : {
          width: columnsWidth?.download_duration || 140,
          field: "download_duration",
          headerName: "Download Duration",
          valueFormatter: (_, row: DownloadBundle) => {
            if (!row.id) {
              return;
            }
            const started = row?.started;
            const finished = row?.finished;
            return calculateDateDuration(started, finished);
          },
          renderCell: ({ row }: { row: DownloadBundle }) => {
            const { started, finished } = row ?? {};
            const duration = calculateDateDuration(started, finished);
            return <>{duration}</>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("queued_duration")
      ? undefined
      : {
          width: columnsWidth?.queued_duration || 140,
          field: "queued_duration",
          headerName: "Queued Duration",
          valueFormatter: (_, row: DownloadBundle) => {
            if (!row.id) {
              return;
            }
            const { created, finished } = row ?? {};
            return calculateDateDuration(created, finished);
          },
          renderCell: ({ row }: { row: DownloadBundle }) => {
            const { created, finished } = row ?? {};
            const duration = calculateDateDuration(created, finished);
            return <>{duration}</>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("project")
      ? undefined
      : {
          flex: columnsWidth?.project ? undefined : 1,
          width: columnsWidth?.project,
          minWidth: 100,
          field: "project",
          headerName: "Project name",
          valueFormatter: (_, row) => {
            // prettier-ignore
            const project =
              row?.parts?.length ? row.parts[0]?.source_table.project :
              row?.source_tables.length > 0 ? row.source_tables[0]?.project :
                "";
            return project;
          },
          renderCell: ({ row }: { row: DownloadBundle }) => {
            // prettier-ignore
            const project =
              row?.parts?.length ? row.parts[0]?.source_table.project :
              row?.source_tables.length > 0 ? row.source_tables[0]?.project :
                "";

            return <>{project}</>;
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("name")
      ? undefined
      : {
          flex: columnsWidth?.name ? undefined : 1,
          width: columnsWidth?.name,
          minWidth: 100,
          field: "name",
          headerName: "Download",
          renderCell: ({ row }: { row: DownloadBundle }) => {
            // prettier-ignore
            const name =
              row?.parts?.length ? row.parts[0]?.source_table.name :
              row.source_tables.length ? row.source_tables[0]?.name :
                "";
            return <>{row?.parts?.length === 1 ? name : name?.split(".")[0]}</>;
          },
          valueFormatter: (_, row: DownloadBundle) => {
            // prettier-ignore
            const name =
              row?.parts?.length ? row.parts[0]?.source_table.name :
              row.source_tables.length ? row.source_tables[0]?.name :
                "";
            return row?.parts?.length === 1 ? name : name?.split(".")[0];
          },
          sortable: false,
          filterable: false,
        },
    excludeFields?.includes("purpose")
      ? undefined
      : {
          width: columnsWidth?.purpose || 100,
          field: "purpose",
          headerName: "Purpose",
          renderCell: ({ row }: { row: DownloadBundle }) => (
            <CenteredDiv>
              <PurposeCell purpose={row?.purpose} />
            </CenteredDiv>
          ),
          valueFormatter: (_, row: DownloadBundle) => {
            return row?.purpose;
          },
          sortable: false,
          filterable: false,
        },
    excludeFields?.includes("description")
      ? undefined
      : {
          flex: columnsWidth?.description ? undefined : 1,
          width: columnsWidth?.description,
          minWidth: 100,
          field: "description",
          headerName: "Description",
          renderCell: ({ row }: { row: DownloadBundle }) => {
            return (
              <span>
                <NameFromHandle>{row?.handle}</NameFromHandle>
              </span>
            );
          },
          valueFormatter: (_, row: DownloadBundle) => {
            const handle = row?.handle;
            return getNameFromHandle(handle);
          },
          filterable: false,
          sortable: false,
        },
    excludeFields?.includes("status")
      ? undefined
      : {
          flex: columnsWidth?.status ? undefined : 1,
          width: columnsWidth?.status,
          minWidth: 100,
          field: "status",
          headerName: "Status",
          headerAlign: "right",
          align: "right",
          renderCell: ({ row }: { row: DownloadBundle }) => {
            return (
              <CenteredDiv>
                {row?.parts?.length === 1 ? (
                  <div style={{ lineHeight: 0, marginLeft: "auto" }}>
                    <DownloadStatusIcon
                      status={row.status}
                      finished={row.finished}
                      dirtyRowCount={row.parts[0]?.dirty_row_count}
                      dirtyRowStatus={row.parts[0]?.dirty_row_status}
                      rowCount={row.parts[0]?.row_count}
                      downloadPartId={row.parts[0]?.id}
                      newDirtyRowCount={row.parts[0]?.new_dirty_row_count}
                      newDirtyRowStatus={row.parts[0]?.new_dirty_row_status}
                      statusColumnWidth={datagridPreferencesElement?.columnsWidth?.status}
                    />
                  </div>
                ) : row?.parts && row.parts.length >= 1 ? (
                  <div style={{ lineHeight: 0, marginLeft: "auto" }}>
                    <DownloadStatusIcon
                      status={row.status}
                      finished={row.finished}
                      statusColumnWidth={datagridPreferencesElement?.columnsWidth?.status}
                      onToggleDetailPanel={() => apiRef.current?.toggleDetailPanel(row.id)}
                      {...getBundleRowCounts(row)}
                    />
                  </div>
                ) : (
                  <DownloadStatusIcon
                    status={row?.status}
                    progress={row?.progress}
                    finished={row.finished}
                    statusColumnWidth={datagridPreferencesElement?.columnsWidth?.status}
                  />
                )}
              </CenteredDiv>
            );
          },
          valueFormatter: (_, row: DownloadBundle) => {
            if (!row?.parts?.length) return row?.status;
            const { dirtyRowCount, rowCount, newDirtyRowCount } = row.parts.reduce(
              (acc, item) => {
                acc.dirtyRowCount += item?.dirty_row_count || 0;
                acc.newDirtyRowCount += item?.new_dirty_row_count || 0;
                acc.rowCount += item?.row_count || 0;
                return acc;
              },
              { dirtyRowCount: 0, newDirtyRowCount: 0, rowCount: 0 }
            );
            if (!dirtyRowCount && !newDirtyRowCount) return `${row?.status} - ${toDefaultInt(rowCount || 0)} rows`;

            const dirtyRowPercentageString = `${toPercentValue({
              d: rowCount,
              n: dirtyRowCount,
            })}% dirty rows`;

            const newDirtyRowPercentageString = `${toPercentValue({
              d: rowCount,
              n: newDirtyRowCount,
            })}% dirty rows (new)`;
            return `${row?.status} - ${dirtyRowPercentageString} - ${newDirtyRowPercentageString}`;
          },
          sortable: false,
          filterable: false,
        },
    excludeFields?.includes("action")
      ? undefined
      : {
          width: columnsWidth?.action || 150,
          field: "action",
          headerName: "Actions",
          align: "right",
          headerAlign: "right",
          filterable: false,
          sortable: false,
          renderCell: ({ row }: { row: DownloadBundle }) => {
            return (
              <DownloadsActionsMenu
                bundleId={row.id}
                bundleStatus={row.status}
                bundleFinishedDate={row.finished}
                part={row?.parts?.length === 1 && row.parts[0] ? row.parts[0] : undefined}
              />
            );
          },
        },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      disableReorder: true,
      renderCell: ({ value, row }: GridRenderCellParams<DownloadBundle & { value: boolean }>) => (
        <ExpandCell isExpanded={value} isBundle={(row?.parts?.length || 0) > 1} status={row.status} />
      ),
    },
  ];

  const columns = columnsAux.filter((column) => column !== undefined);

  const columnsCopy = columns.slice();
  if (datagridPreferencesElement?.columnsOrder) {
    columnsCopy.sort((a, b) => {
      const aIndex = datagridPreferencesElement?.columnsOrder?.indexOf(a.field) ?? -1;
      const bIndex = datagridPreferencesElement?.columnsOrder?.indexOf(b.field) ?? -1;
      return aIndex - bIndex;
    });
  }

  const displayedColumns = columnsCopy;

  return (
    <DataGridPro
      apiRef={apiRef}
      disableColumnFilter
      loading={loading}
      rows={rows}
      columns={displayedColumns}
      disableColumnPinning
      disableDensitySelector
      onRowClick={handleRowClick}
      slots={{
        columnsPanel: CustomColumnsPanel,
        noRowsOverlay: EmptyRows,
      }}
      onColumnWidthChange={(column) =>
        customColumnWidthChange(column, preferenceElementToUpdate, user, updateDataGridPreferences)
      }
      onColumnOrderChange={(column) => {
        customOnColumnOrderChange(column, preferenceElementToUpdate, user, updateDataGridPreferences, displayedColumns);
      }}
      columnVisibilityModel={datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel}
      onColumnVisibilityModelChange={(model) => {
        updateDataGridPreferences(preferenceElementToUpdate, {
          columnsOrder: datagridPreferencesElement?.columnsOrder,
          columnsWidth: columnsWidth,
          columnVisibilityModel: model,
        });
      }}
      getDetailPanelContent={({ row }: GridRowParams<DownloadBundle>) => (
        <DownloadRowsForBundle bundle={row} statusColumnWidth={datagridPreferencesElement?.columnsWidth?.status} />
      )}
      getDetailPanelHeight={() => "auto"}
      disableColumnSelector
      filterMode="server"
      onRowsScrollEnd={onRowsScrollEnd}
      onSortModelChange={onSortModelChange}
      initialState={{
        columns: {
          columnVisibilityModel: datagridPreferencesElement?.columnVisibilityModel || initialVisibilityModel,
        },
        sorting: {
          sortModel: getInitialSortModel(filter),
        },
      }}
      sortingMode="server"
      sx={{
        "& .MuiDataGrid-panelContent": {
          height: "400px",
        },
      }}
      getRowHeight={(params: GridRowHeightParams) => {
        const model = params.model as DownloadBundle;
        const ROW_WITH_DIRTY_ROWS_HEIGHT = 92;
        if (!model?.parts?.length) return undefined;
        const { parts } = model;

        const isSimpleRowDirty =
          parts.length === 1 &&
          parts[0]?.new_dirty_row_status &&
          ["some", "all"].includes(parts[0].new_dirty_row_status);

        const isSimpleRowNewDirty =
          parts.length === 1 && parts[0]?.dirty_row_status && ["some", "all"].includes(parts[0].dirty_row_status);

        if (isSimpleRowDirty || isSimpleRowNewDirty) {
          return ROW_WITH_DIRTY_ROWS_HEIGHT;
        }

        const bundleRowCounts = getBundleRowCounts(model);

        if (!bundleRowCounts) return undefined;

        const { newDirtyRowStatus, dirtyRowStatus } = bundleRowCounts;

        const isBundleRowDirty = newDirtyRowStatus && ["some", "all"].includes(newDirtyRowStatus);
        const isBundleRowNewDirty = dirtyRowStatus && ["some", "all"].includes(dirtyRowStatus);
        if (isBundleRowDirty || isBundleRowNewDirty) {
          return ROW_WITH_DIRTY_ROWS_HEIGHT;
        }

        return undefined;
      }}
      slotProps={{
        root: { "data-testid": "downloads-table" },
        loadingOverlay: {
          variant: "linear-progress",
          noRowsVariant: "linear-progress",
        },
        noRowsOverlay: {
          isFiltering: isFilteringDownloads(filter),
        },
        row: {
          "data-testid": "download-row",
          style: {
            cursor: "pointer",
          },
        },
      }}
      getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
      hideFooter
    />
  );
};

export { DownloadsDataGrid };
